import httpRequest from '@/utils/api'
import type {
  DialogueBook,
  DialogueBookCreateRequest,
  DialogueBookListParams,
  DialogueBookListResponseData,
  DialogueBookUpdateRequest,
  DialogueChapter,
  DialogueChapterCreateRequest,
  DialogueChapterListResponseData,
  DialogueChapterUpdateRequest,
  DialogueContentListResponseData,
  DialogueMediaAsset,
  DialogueMediaAssetCreateRequest,
  DialogueMediaAssetListResponseData,
  DialogueMediaAssetUpdateRequest,
  DialogueSegment,
  DialogueSegmentCreateRequest,
  DialogueSegmentListResponseData,
  DialogueSegmentUpdateRequest,
  DialogueTarget,
  DialogueTargetBindContentRequest,
  DialogueTargetCreateRequest,
  DialogueTargetUpdateRequest,
} from '@/types/dictation/dialogue'

enum API {
  books = '/dictation-api/admin/dictation/dialogue/books',
  book = '/dictation-api/admin/dictation/dialogue/books/:bookId',
  chapters = '/dictation-api/admin/dictation/dialogue/books/:bookId/chapters',
  chapter = '/dictation-api/admin/dictation/dialogue/chapters/:chapterId',
  mediaAssets = '/dictation-api/admin/dictation/dialogue/chapters/:chapterId/media-assets',
  mediaAsset = '/dictation-api/admin/dictation/dialogue/media-assets/:assetId',
  segments = '/dictation-api/admin/dictation/dialogue/chapters/:chapterId/segments',
  segment = '/dictation-api/admin/dictation/dialogue/segments/:segmentId',
  createSegment = '/dictation-api/admin/dictation/dialogue/media-assets/:assetId/segments',
  createTarget = '/dictation-api/admin/dictation/dialogue/segments/:segmentId/targets',
  target = '/dictation-api/admin/dictation/dialogue/targets/:targetId',
  bindTargetContent = '/dictation-api/admin/dictation/dialogue/targets/:targetId/content',
  searchContents = '/dictation-api/admin/dictation/dialogue/contents/search',
}

export const getDialogueBookListAPI = (params?: DialogueBookListParams) => {
  return httpRequest.get<DialogueBookListResponseData>({
    url: API.books,
    params,
  })
}

export const getDialogueBookAPI = (bookId: number) => {
  return httpRequest.get<DialogueBook>({
    url: API.book.replace(':bookId', String(bookId)),
  })
}

export const createDialogueBookAPI = (data: DialogueBookCreateRequest) => {
  return httpRequest.post<DialogueBook>({
    url: API.books,
    data,
  })
}

export const updateDialogueBookAPI = (bookId: number, data: DialogueBookUpdateRequest) => {
  return httpRequest.put<DialogueBook>({
    url: API.book.replace(':bookId', String(bookId)),
    data,
  })
}

export const deleteDialogueBookAPI = (bookId: number) => {
  return httpRequest.delete<void>({
    url: API.book.replace(':bookId', String(bookId)),
  })
}

export const getDialogueChapterListAPI = (bookId: number) => {
  return httpRequest.get<DialogueChapterListResponseData>({
    url: API.chapters.replace(':bookId', String(bookId)),
  })
}

export const getDialogueChapterAPI = (chapterId: number) => {
  return httpRequest.get<DialogueChapter>({
    url: API.chapter.replace(':chapterId', String(chapterId)),
  })
}

export const createDialogueChapterAPI = (bookId: number, data: DialogueChapterCreateRequest) => {
  return httpRequest.post<DialogueChapter>({
    url: API.chapters.replace(':bookId', String(bookId)),
    data,
  })
}

export const updateDialogueChapterAPI = (chapterId: number, data: DialogueChapterUpdateRequest) => {
  return httpRequest.put<DialogueChapter>({
    url: API.chapter.replace(':chapterId', String(chapterId)),
    data,
  })
}

export const deleteDialogueChapterAPI = (chapterId: number) => {
  return httpRequest.delete<void>({
    url: API.chapter.replace(':chapterId', String(chapterId)),
  })
}

export const getDialogueMediaAssetsAPI = (chapterId: number) => {
  return httpRequest.get<DialogueMediaAssetListResponseData>({
    url: API.mediaAssets.replace(':chapterId', String(chapterId)),
  })
}

export const createDialogueMediaAssetAPI = (
  chapterId: number,
  data: DialogueMediaAssetCreateRequest
) => {
  return httpRequest.post<DialogueMediaAsset>({
    url: API.mediaAssets.replace(':chapterId', String(chapterId)),
    data,
  })
}

export const updateDialogueMediaAssetAPI = (
  assetId: number,
  data: DialogueMediaAssetUpdateRequest
) => {
  return httpRequest.put<DialogueMediaAsset>({
    url: API.mediaAsset.replace(':assetId', String(assetId)),
    data,
  })
}

export const deleteDialogueMediaAssetAPI = (assetId: number) => {
  return httpRequest.delete<void>({
    url: API.mediaAsset.replace(':assetId', String(assetId)),
  })
}

export const getDialogueSegmentsAPI = (chapterId: number) => {
  return httpRequest.get<DialogueSegmentListResponseData>({
    url: API.segments.replace(':chapterId', String(chapterId)),
  })
}

export const getDialogueSegmentAPI = (segmentId: number) => {
  return httpRequest.get<DialogueSegment>({
    url: API.segment.replace(':segmentId', String(segmentId)),
  })
}

export const createDialogueSegmentAPI = (
  assetId: number,
  data: DialogueSegmentCreateRequest
) => {
  return httpRequest.post<DialogueSegment>({
    url: API.createSegment.replace(':assetId', String(assetId)),
    data,
  })
}

export const updateDialogueSegmentAPI = (
  segmentId: number,
  data: DialogueSegmentUpdateRequest
) => {
  return httpRequest.put<DialogueSegment>({
    url: API.segment.replace(':segmentId', String(segmentId)),
    data,
  })
}

export const deleteDialogueSegmentAPI = (segmentId: number) => {
  return httpRequest.delete<void>({
    url: API.segment.replace(':segmentId', String(segmentId)),
  })
}

export const createDialogueTargetAPI = (
  segmentId: number,
  data: DialogueTargetCreateRequest
) => {
  return httpRequest.post<DialogueTarget>({
    url: API.createTarget.replace(':segmentId', String(segmentId)),
    data,
  })
}

export const updateDialogueTargetAPI = (
  targetId: number,
  data: DialogueTargetUpdateRequest
) => {
  return httpRequest.put<DialogueTarget>({
    url: API.target.replace(':targetId', String(targetId)),
    data,
  })
}

export const deleteDialogueTargetAPI = (targetId: number) => {
  return httpRequest.delete<void>({
    url: API.target.replace(':targetId', String(targetId)),
  })
}

export const bindDialogueTargetContentAPI = (
  targetId: number,
  data: DialogueTargetBindContentRequest
) => {
  return httpRequest.put<DialogueTarget>({
    url: API.bindTargetContent.replace(':targetId', String(targetId)),
    data,
  })
}

export const searchDialogueContentsAPI = (
  keyword?: string,
  languageId?: number,
  limit = 20
) => {
  return httpRequest.get<DialogueContentListResponseData>({
    url: API.searchContents,
    params: {
      keyword,
      language_id: languageId,
      limit,
    },
  })
}
